import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PreviewTemplate from 'components/common/previewTemplate';
import { InlineFeedback } from 'components/forms';
import { useFeedback } from 'contexts/FeedbackContext';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { type FC, useCallback, useEffect, useState } from 'react';
import type { TemplateCreate, WhatsAppButton } from 'services/api/businessUnits.api';
import { useCreateBusinessUnitTemplateMutation, useUploadTemplateFileMutation } from 'services/api/businessUnits.api';
import type { Client } from 'services/types/client';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  YELLOW,
} from 'styles/tokens';

// Common emojis matrix
const COMMON_EMOJIS = [
  ['😊', '👍', '🎉', '❤️', '🙏', '✨', '💪', '🤝'],
  ['👋', '🌟', '💯', '🔥', '👏', '🎯', '✅', '🚗'],
  ['📱', '💼', '📊', '📈', '🎁', '🛒', '💰', '📝'],
  ['🔔', '⏰', '📅', '🌈', '🚀', '🏆', '💡', '🔍'],
];

const DEFAULT_BODY_TEXT =
  'Hola {{cliente}}!\nSoy {{usuario}}, te escribo para retomar nuestra conversación anterior. \n\n¿Quieres que te siga ayudando con tu compra?';

const DEFAULT_BUTTONS: WhatsAppButton[] = [
  { type: 'QUICK_REPLY', text: 'Si, me interesa' },
  { type: 'QUICK_REPLY', text: 'Desactivar marketing' },
];

type VariableExample = { param_name: string; example: string };

const MAX_BUTTON_LENGTH = 25;
const MAX_VARIABLE_LENGTH = 25;
const MAX_FOOTER_LENGTH = 60;

interface Props {
  businessUnitId: number;
  currentClient?: Client;
}

const errorWithBodyAndVariables = (text: string, bodyVariableExamples: VariableExample[]): string | null => {
  // Count variables in text (both numbered {{1}} and named {{variable_name}})
  const variables = [...text.matchAll(/\{\{([a-z0-9_]+)\}\}/g)].map((v) => v[1]);
  if (variables.length === 0 && bodyVariableExamples.length === 0) return null;

  const variablesSet = new Set(variables);
  const uniqueVariables = Array.from(variablesSet);

  if (uniqueVariables.length !== bodyVariableExamples.length) {
    return 'Las cantidad de variables no coincide con la cantidad de variables en el texto';
  }

  for (let i = 0; i < uniqueVariables.length; i += 1) {
    if (!bodyVariableExamples.some((v) => v.param_name === uniqueVariables[i])) {
      return `La variable ${uniqueVariables[i]} no está definida correctamente`;
    }
  }

  // Count total words in the text
  const words = text.trim().split(/\s+/);

  // Need at least 4 words per variable
  return words.length >= variables.length * 4 ? null : `Debe haber al menos 4 palabras por cada variable en el texto`;
};

const CreateTemplateForm: FC<Props> = ({ businessUnitId, currentClient }) => {
  const [createTemplate, { isLoading, isSuccess, isError }] = useCreateBusinessUnitTemplateMutation();
  const [uploadTemplateFile] = useUploadTemplateFileMutation();
  const currentUser = useCurrentUser();
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState<'UTILITY' | 'MARKETING'>('MARKETING');
  const language = 'es';
  const parameterFormat = 'named';

  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerFormat, setHeaderFormat] = useState<'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT'>('TEXT');
  const [headerFile, setHeaderFile] = useState<File | null>(null);
  const [headerText, setHeaderText] = useState('');
  const [headerFileUrl, setHeaderFileUrl] = useState<string | null>(null);

  const [bodyText, setBodyText] = useState(DEFAULT_BODY_TEXT);
  const [bodyVariableExamples, setBodyVariableExamples] = useState<VariableExample[]>([
    {
      param_name: 'cliente',
      example: currentClient?.firstName || 'Nombre del Cliente',
    },
    {
      param_name: 'usuario',
      example: currentUser?.name || 'Nombre del Usuario',
    },
  ]);

  const [footerEnabled, setFooterEnabled] = useState(false);
  const [footerText, setFooterText] = useState('');

  const [buttons, setButtons] = useState<WhatsAppButton[]>(DEFAULT_BUTTONS);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { notifySaved } = useFeedback();

  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [variableModalOpen, setVariableModalOpen] = useState(false);
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableExample, setNewVariableExample] = useState('');

  const transformTemplateName = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9_]/g, '_');
  };

  const addVariable = () => {
    setVariableModalOpen(true);
  };

  const handleAddVariableSubmit = () => {
    if (!newVariableName || !newVariableExample) return;

    const bodyInput = document.querySelector('textarea[name="BODY"]') as HTMLTextAreaElement;
    const cursorPosition = bodyInput.selectionStart;
    const newVariable = `{{${newVariableName}}}`;
    const newText = bodyText.slice(0, cursorPosition) + newVariable + bodyText.slice(cursorPosition);
    setBodyText(newText);
    setBodyVariableExamples([...bodyVariableExamples, { param_name: newVariableName, example: newVariableExample }]);

    // Reset modal state
    setVariableModalOpen(false);
    setNewVariableName('');
    setNewVariableExample('');

    // Focus the input again after adding a variable
    setTimeout(() => {
      if (bodyInput) {
        bodyInput.focus();
        const newCursorPosition = cursorPosition + newVariable.length;
        bodyInput.selectionStart = newCursorPosition;
        bodyInput.selectionEnd = newCursorPosition;
      }
    }, 0);
  };

  const handleExampleChange = (index: number, value: string) => {
    const newExamples = [...bodyVariableExamples];
    newExamples[index].example = value;
    setBodyVariableExamples(newExamples);
  };

  const removeVariable = useCallback(
    (indexToRemove: number) => {
      const variableToRemove = bodyVariableExamples[indexToRemove];
      // Remove the variable from the text
      const newText = bodyText.replaceAll(`{{${variableToRemove.param_name}}}`, '');
      setBodyText(newText);

      // Remove the example and update state
      const newExamples = bodyVariableExamples.filter((_, index) => index !== indexToRemove);
      setBodyVariableExamples(newExamples);
    },
    [bodyText, bodyVariableExamples]
  );

  const handleNewTemplateSubmit = () => {
    const template: TemplateCreate = {
      name: templateName,
      category: templateCategory,
      parameter_format: parameterFormat,
      language,
      components: [],
    };

    if (headerEnabled) {
      template.components.push({
        type: 'HEADER',
        text: headerFormat === 'TEXT' ? headerText : undefined,
        format: headerFormat,
        example:
          headerFormat !== 'TEXT'
            ? {
                header_handle: [headerFileUrl as string],
              }
            : undefined,
      });
    }

    template.components.push({
      type: 'BODY',
      text: bodyText,
      example:
        bodyVariableExamples.length > 0
          ? {
              body_text_named_params: bodyVariableExamples,
            }
          : undefined,
    });

    if (footerEnabled) {
      template.components.push({
        type: 'FOOTER',
        text: footerText,
      });
    }

    if (buttons.length > 0) {
      template.components.push({
        type: 'BUTTONS',
        buttons,
      });
    }

    createTemplate({
      businessUnitId,
      template,
    }).catch((err) => {
      console.error(err);
    });
  };

  // Handle template creation success
  useEffect(() => {
    if (!isSuccess) return () => {};

    notifySaved('Plantilla creada');
    const timer = setTimeout(() => {
      window.location.reload();
    }, 4000);

    return () => clearTimeout(timer);
  }, [isSuccess, notifySaved]);

  // Handle template creation error
  useEffect(() => {
    if (isError) {
      console.error('Error creating template');
      setErrorMessage('Error al crear la plantilla. Inténtalo nuevamente.');
    }
  }, [isError]);

  let isFormValid = true;
  const bodyError = errorWithBodyAndVariables(bodyText, bodyVariableExamples);
  if (
    !templateName?.match(/^[a-z0-9_]{5,40}$/) ||
    !bodyText ||
    bodyError ||
    bodyVariableExamples.some((v) => !v.param_name || !v.example) ||
    buttons.some((button) => !button.text) ||
    (headerEnabled && headerFormat === 'TEXT' && !headerText) ||
    (headerEnabled && headerFormat !== 'TEXT' && !headerFileUrl) ||
    (footerEnabled && !footerText)
  ) {
    isFormValid = false;
  }

  const handleAddFormatting = useCallback((char: string) => {
    const textarea = document.querySelector('textarea[name="BODY"]') as HTMLTextAreaElement;
    if (!textarea) {
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const newText = text.substring(0, start) + char + text.substring(start, end) + char + text.substring(end);

    setBodyText(newText);

    // Set cursor position between the formatting characters
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + 1, end + 1);
    }, 0);
  }, []);

  const handleEmojiClick = useCallback((emoji: string) => {
    const textarea = document.querySelector('textarea[name="BODY"]') as HTMLTextAreaElement;
    if (!textarea) {
      return;
    }
    const start = textarea.selectionStart;
    const text = textarea.value;
    const newText = text.substring(0, start) + emoji + text.substring(start);

    setBodyText(newText);

    // Set cursor position after the emoji
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + emoji.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
    setEmojiAnchorEl(null);
  }, []);

  const handleHeaderFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (headerFormat === 'IMAGE' && !file.type.startsWith('image/')) {
      setErrorMessage('El archivo debe ser una imagen.');
      return;
    }
    if (headerFormat === 'DOCUMENT' && file.type !== 'application/pdf') {
      setErrorMessage('El archivo debe ser un PDF.');
      return;
    }
    if (headerFormat === 'VIDEO' && file.type !== 'video/mp4') {
      setErrorMessage('El archivo debe ser un video MP4.');
      return;
    }

    // Validate file size
    const maxSize = headerFormat === 'IMAGE' ? 1_000_000 : 6_000_000; // 1MB for images, 5MB for documents
    if (file.size > maxSize) {
      setErrorMessage(headerFormat === 'IMAGE' ? 'La imagen pesa más de 1MB.' : 'El archivo pesa más de 6MB.');
      return;
    }

    setErrorMessage(null);
    setHeaderFile(file);
    uploadTemplateFile({ businessUnitId, file })
      .unwrap()
      .then((response) => {
        setHeaderFileUrl(response.url);
      })
      .catch(() => {
        setErrorMessage('Error al subir el archivo. Inténtalo nuevamente.');
        setHeaderFile(null);
      });
  };

  const getFileValidationError = () => {
    if (!headerFile) return null;

    if (headerFormat === 'IMAGE') {
      if (!headerFile.type.startsWith('image/')) {
        return 'Solo se permiten archivos de imagen (JPEG, PNG)';
      }
      if (headerFile.size > 1024 * 1024) {
        return 'La imagen debe pesar menos de 1MB';
      }
    }

    if (headerFormat === 'DOCUMENT') {
      if (headerFile.type !== 'application/pdf') {
        return 'Solo se permiten archivos PDF';
      }
      if (headerFile.size > 6 * 1024 * 1024) {
        // 6MB, but saying 5MB to be safe
        return 'El documento debe pesar menos de 5MB';
      }
    }

    if (headerFormat === 'VIDEO') {
      if (headerFile.type !== 'video/mp4') {
        return 'Solo se permiten archivos de video MP4';
      }
      if (headerFile.size > 20 * 1024 * 1024) {
        return 'El video debe pesar menos de 20MB';
      }
    }

    return null;
  };

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: 1400,
        mx: 'auto',
      }}
    >
      {errorMessage && (
        <InlineFeedback severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </InlineFeedback>
      )}
      {isSuccess && (
        <InlineFeedback severity="success" sx={{ mb: 2 }}>
          Plantilla creada. WhatsApp la revisará en breve y la página se actualizará en unos segundos.
        </InlineFeedback>
      )}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            autoFocus
            label="Nombre de la plantilla"
            value={templateName}
            onChange={(e) => setTemplateName(transformTemplateName(e.target.value))}
            required
            fullWidth
            sx={{ mb: 2 }}
            error={templateName.length < 5 || templateName.length > 40}
            helperText={
              templateName.length < 5 || templateName.length > 40
                ? 'El nombre debe tener entre 5 y 40 caracteres y solo puede contener letras minúsculas, números y guiones bajos'
                : ''
            }
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Categoría</InputLabel>
            <Select
              value={templateCategory}
              label="Categoría"
              onChange={(e) => setTemplateCategory(e.target.value as 'UTILITY' | 'MARKETING')}
            >
              <MenuItem value="UTILITY">Utility</MenuItem>
              <MenuItem value="MARKETING">Marketing</MenuItem>
            </Select>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mt: 1,
              }}
            >
              Utility: Solo para temas específicos como recordatorios de visitas, encuesta de satisfacción, etc.
              <br />
              Marketing: Para la mayoría de los casos en que se busca convencer al cliente de comprar/contratar un
              servicio.
            </Typography>
          </FormControl>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: SURFACE_CONTAINER_LOW,
              borderRadius: RADIUS_CARD,
              mb: 2,
            }}
          >
            <Box
              sx={{
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">Encabezado (opcional)</Typography>
                <Switch
                  checked={headerEnabled}
                  onChange={(e) => setHeaderEnabled(e.target.checked)}
                  size="small"
                  sx={{ ml: 1 }}
                />
                {!headerEnabled && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Habilita el encabezado para agregar un título, imagen, video o pdf al inicio del mensaje.
                  </Typography>
                )}
              </Box>
              {headerEnabled && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Formato</InputLabel>
                    <Select
                      value={headerFormat}
                      label="Formato"
                      onChange={(e) => {
                        const format = e.target.value as 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
                        setHeaderFormat(format);
                        setHeaderFile(null);
                        setHeaderFileUrl(null);
                      }}
                    >
                      <MenuItem value="TEXT">Texto</MenuItem>
                      <MenuItem value="IMAGE">Imagen</MenuItem>
                      <MenuItem value="VIDEO">Video</MenuItem>
                      <MenuItem value="DOCUMENT">Documento</MenuItem>
                    </Select>
                  </FormControl>
                  {headerFormat === 'TEXT' ? (
                    <TextField
                      name="HEADER"
                      value={headerText}
                      onChange={(e) => setHeaderText(e.target.value)}
                      fullWidth
                      placeholder="Título"
                      helperText="Se verá en negrita y más grande al inicio del mensaje"
                    />
                  ) : (
                    <Box>
                      <input
                        type="file"
                        accept={`${headerFormat === 'IMAGE' ? 'image/png,image/jpeg,image/jpg' : ''}${
                          headerFormat === 'VIDEO' ? 'video/mp4' : ''
                        }${headerFormat === 'DOCUMENT' ? 'application/pdf' : ''}`}
                        onChange={handleHeaderFileChange}
                        style={{ marginBottom: '8px' }}
                      />
                      {headerFileUrl && (
                        <Box
                          sx={{
                            mt: 1,
                          }}
                        >
                          {headerFormat === 'IMAGE' && (
                            <img src={headerFileUrl} alt="Header" style={{ maxWidth: '200px' }} />
                          )}
                          {headerFormat === 'VIDEO' && (
                            <video width="100%" autoPlay muted controls>
                              <source src={headerFileUrl} type={headerFile?.type || 'video/mp4'} />
                            </video>
                          )}
                          {headerFormat === 'DOCUMENT' && <Typography variant="body2">Documento cargado</Typography>}
                        </Box>
                      )}
                      {getFileValidationError() && (
                        <Typography color="error" variant="caption">
                          {getFileValidationError()}
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
            <Box
              sx={{
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                }}
              >
                Cuerpo del mensaje
              </Typography>
              <TextField
                multiline
                name="BODY"
                minRows={4}
                maxRows={10}
                value={bodyText}
                onChange={(e) => {
                  setBodyText(e.target.value);
                }}
                fullWidth
                placeholder="Escribe tu mensaje aquí"
                helperText={bodyError}
                error={!!bodyError}
              />
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                }}
              >
                <Tooltip title="Negrita">
                  <IconButton size="small" onClick={() => handleAddFormatting('*')}>
                    <FormatBoldIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cursiva">
                  <IconButton size="small" onClick={() => handleAddFormatting('_')}>
                    <FormatItalicIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tachado">
                  <IconButton size="small" onClick={() => handleAddFormatting('~')}>
                    <FormatStrikethroughIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Emoji">
                  <IconButton size="small" onClick={(e) => setEmojiAnchorEl(e.currentTarget)}>
                    <InsertEmoticonIcon />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(emojiAnchorEl)}
                  anchorEl={emojiAnchorEl}
                  onClose={() => setEmojiAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <Grid>
                    {COMMON_EMOJIS.map((row) => (
                      <Grid container key={row[0]} spacing={1}>
                        {row.map((emoji) => (
                          <Grid key={emoji}>
                            <IconButton
                              size="small"
                              onClick={() => handleEmojiClick(emoji)}
                              sx={{
                                fontSize: '1.5rem',
                              }}
                            >
                              {emoji}
                            </IconButton>
                          </Grid>
                        ))}
                      </Grid>
                    ))}
                  </Grid>
                </Popover>
                <Button
                  variant="outlined"
                  onClick={addVariable}
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{ ml: 'auto' }}
                >
                  Agregar variable
                </Button>
              </Box>
              {bodyVariableExamples.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Variables:
                  </Typography>
                  <Stack spacing={1.5}>
                    {bodyVariableExamples.map((variable, varIndex) => (
                      <Box
                        key={`${varIndex + 1}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <TextField
                          label={variable.param_name}
                          value={variable.example}
                          onChange={(e) => {
                            const value = e.target.value.slice(0, MAX_VARIABLE_LENGTH);
                            handleExampleChange(varIndex, value);
                          }}
                          fullWidth
                          size="small"
                          helperText={
                            !variable.example
                              ? 'El ejemplo no puede estar vacío'
                              : `${variable.example.length}/${MAX_VARIABLE_LENGTH}`
                          }
                          error={!variable.example}
                          slotProps={{
                            htmlInput: { maxLength: MAX_VARIABLE_LENGTH },
                          }}
                        />
                        <Button onClick={() => removeVariable(varIndex)} color="error" sx={{ minWidth: 'auto' }}>
                          <DeleteIcon />
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">Pie de página (opcional)</Typography>
                <Switch
                  checked={footerEnabled}
                  onChange={(e) => setFooterEnabled(e.target.checked)}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Box>
              {footerEnabled && (
                <TextField
                  value={footerText}
                  onChange={(e) => {
                    setFooterText(e.target.value);
                  }}
                  fullWidth
                  placeholder="Pie de página"
                  helperText={`Se verá en gris claro, sirve para poner el nombre de la empresa o persona. ${footerText.length}/${MAX_FOOTER_LENGTH}`}
                  slotProps={{
                    htmlInput: { maxLength: MAX_FOOTER_LENGTH },
                  }}
                />
              )}
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Botones
              </Typography>
              <Stack spacing={1}>
                {buttons.map((button, buttonIndex) => (
                  <Box
                    key={`${buttonIndex + 1}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <TextField
                      label={`Botón ${buttonIndex + 1}`}
                      value={button.text}
                      onChange={(e) => {
                        const newButtons = [...buttons];
                        newButtons[buttonIndex] = { type: 'QUICK_REPLY', text: e.target.value };
                        setButtons(newButtons);
                      }}
                      size="small"
                      fullWidth
                      helperText={`${button.text.length}/${MAX_BUTTON_LENGTH}`}
                      slotProps={{
                        htmlInput: { maxLength: MAX_BUTTON_LENGTH },
                      }}
                    />
                    <Button
                      onClick={() => {
                        const newButtons = buttons.filter((_, i) => i !== buttonIndex);
                        setButtons(newButtons);
                      }}
                      color="error"
                      sx={{ minWidth: 'auto' }}
                    >
                      <DeleteIcon />
                    </Button>
                  </Box>
                ))}
                <Button
                  onClick={() => setButtons([...buttons, { type: 'QUICK_REPLY', text: '' }])}
                  startIcon={<AddIcon />}
                  disabled={buttons.length >= 3}
                  sx={{
                    borderRadius: RADIUS_COIN,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: NAVY,
                    backgroundColor: alpha(NAVY, 0.06),
                    px: 2,
                    '&:hover': { backgroundColor: alpha(NAVY, 0.1) },
                    '&.Mui-disabled': { color: alpha(NAVY, 0.3), backgroundColor: alpha(NAVY, 0.04) },
                  }}
                >
                  Agregar Botón
                </Button>
              </Stack>
            </Box>
          </Paper>

          <Button
            size="large"
            onClick={handleNewTemplateSubmit}
            disabled={!isFormValid || isLoading}
            sx={{
              mt: 1.5,
              borderRadius: RADIUS_COIN,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: YELLOW,
              color: NAVY,
              boxShadow: 'none',
              px: 3,
              minWidth: 180,
              '&:hover': { backgroundColor: YELLOW, opacity: 0.9, boxShadow: SHADOW },
              '&.Mui-disabled': { backgroundColor: alpha(NAVY, 0.08), color: alpha(NAVY, 0.3) },
            }}
          >
            {isLoading ? 'Creando…' : 'Crear plantilla'}
          </Button>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 1,
            }}
          >
            Después de crear la plantilla, podrás utilizarlo para enviar mensajes a tus clientes desde la pestaña
            &quot;Enviar Plantilla&quot;.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <PreviewTemplate
            headerEnabled={headerEnabled}
            headerText={headerText}
            headerFormat={headerFormat}
            headerFileUrl={headerFileUrl}
            bodyText={bodyText}
            bodyVariableExamples={bodyVariableExamples}
            footerEnabled={footerEnabled}
            footerText={footerText}
            buttons={buttons}
          />
        </Grid>
      </Grid>
      <Dialog
        open={variableModalOpen}
        onClose={() => {
          setVariableModalOpen(false);
          setNewVariableName('');
          setNewVariableExample('');
        }}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: GLASS_BACKGROUND,
              backdropFilter: GLASS_BLUR,
              borderRadius: RADIUS_CARD,
              boxShadow: SHADOW,
              border: `1px solid ${alpha(NAVY, 0.06)}`,
            },
          },
        }}
      >
        <DialogTitle sx={{ color: NAVY, fontWeight: 700 }}>Agregar variable</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              pt: 1,
            }}
          >
            <TextField
              autoFocus
              label="Nombre de la variable"
              value={newVariableName}
              onChange={(e) => {
                const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
                setNewVariableName(value);
              }}
              fullWidth
              sx={{ mb: 2 }}
              helperText={
                bodyVariableExamples.some((v) => v.param_name === newVariableName)
                  ? 'Ya existe una variable con este nombre'
                  : 'Solo letras minúsculas, números y guiones bajos (ej: marca, fecha_visita). "client" y "user" son especiales, reemplaza por defecto al nombre del cliente y el usuario respectivamente.'
              }
              error={
                (newVariableName.length > 0 && !newVariableName.match(/^[a-z0-9_]+$/)) ||
                bodyVariableExamples.some((v) => v.param_name === newVariableName)
              }
            />
            <TextField
              label="Ejemplo de valor"
              value={newVariableExample}
              onChange={(e) => setNewVariableExample(e.target.value.slice(0, MAX_VARIABLE_LENGTH))}
              fullWidth
              helperText={`Este es un valor de ejemplo para previsualizar la plantilla. ${newVariableExample.length}/${MAX_VARIABLE_LENGTH}`}
              slotProps={{
                htmlInput: { maxLength: MAX_VARIABLE_LENGTH },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => {
              setVariableModalOpen(false);
              setNewVariableName('');
              setNewVariableExample('');
            }}
            sx={{
              color: NAVY,
              borderRadius: RADIUS_COIN,
              textTransform: 'none',
              fontWeight: 500,
              px: 2.5,
              '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAddVariableSubmit}
            disabled={
              !newVariableName ||
              !newVariableExample ||
              !newVariableName.match(/^[a-z0-9_]+$/) ||
              bodyVariableExamples.some((v) => v.param_name === newVariableName)
            }
            sx={{
              backgroundColor: YELLOW,
              color: NAVY,
              borderRadius: RADIUS_COIN,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              boxShadow: 'none',
              '&:hover': { backgroundColor: YELLOW, opacity: 0.9, boxShadow: SHADOW },
              '&.Mui-disabled': { backgroundColor: alpha(NAVY, 0.08), color: alpha(NAVY, 0.3) },
            }}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateTemplateForm;
